import { useCallback } from 'react';
import { useChatThreads, ChatThread, deleteChatThread, updateChatThreadName } from '@stores/data.store';
import PliableLoader from '@components/loaders/PliableLoader.component';
import { requireConfirmation } from '@services/alert/alert.service';
import InlineEditableText from '@components/general/InlineEditableText.component';
import styled from 'styled-components';
import LoadingCard from '@components/card/LoadingCard.component';
import { timeAgo, parseDateString } from '@services/time.service';

interface ChatHistoryListProps {
    onSelectThread: (thread: ChatThread) => void;
    onNewChat?: () => void;
    showHeader?: boolean;
    activeThreadId?: string;
}

const Styles = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;

    .chat-history-header {
        padding: 12px 16px;
        border-bottom: 1px solid var(--ct-border-color);
        display: flex;
        align-items: center;
        justify-content: space-between;

        h5 {
            margin: 0;
            display: flex;
            align-items: center;
            gap: 8px;
        }
    }

    .chat-history-list {
        flex: 1;
        overflow: auto;
        min-height: 0;
    }

    .thread-item {
        cursor: pointer;
        transition: background-color 0.15s;

        .delete-btn {
            opacity: 0;
            transition: opacity 0.15s;
        }

        &:hover {
            background-color: var(--ct-light);

            .delete-btn {
                opacity: 1;
            }
        }

        &.active {
            background-color: #f0f0f0;
            border-left: 3px solid var(--ct-primary);
            color: inherit;

            .text-muted {
                color: var(--bs-secondary-color) !important;
            }

            .btn-link {
                color: var(--bs-secondary-color) !important;
            }

            &:hover {
                background-color: #e8e8e8;
            }
        }
    }
`;


export const ChatHistoryList = ({
    onSelectThread,
    onNewChat,
    showHeader = true,
    activeThreadId
}: ChatHistoryListProps) => {
    const { data: chatThreads, isLoading: threadsLoading } = useChatThreads();

    const handleDeleteThread = useCallback(async (e: React.MouseEvent, thread: ChatThread) => {
        e.stopPropagation();
        const confirmed = await requireConfirmation(
            `Are you sure you want to delete "${thread.name || 'New Chat'}"?`,
            'Delete Chat',
            'Delete',
            'Cancel',
            undefined,
            'danger'
        );
        if (confirmed) {
            await deleteChatThread(thread.thread_id);
        }
    }, []);

    const handleRenameThread = useCallback(async (thread: ChatThread, newName: string) => {
        await updateChatThreadName(thread.thread_id, newName);
    }, []);

    return (
        <Styles>
            {showHeader && (
                <div className="chat-history-header">
                    <h5>
                        <i className="mdi mdi-comment-multiple"></i>
                        Your Chats
                    </h5>
                    {onNewChat && (
                        <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={onNewChat}
                        >
                            <i className="mdi mdi-plus me-1"></i>
                            New Chat
                        </button>
                    )}
                </div>
            )}
            <div className="chat-history-list">
                {threadsLoading && (
                    <div className="text-center py-4">
                        <LoadingCard action="Loading chats" />
                    </div>
                )}
                {!threadsLoading && (!chatThreads || chatThreads.length === 0) && (
                    <div className="text-muted text-center py-4">
                        <i className="mdi mdi-chat-outline d-block mb-2" style={{ fontSize: '2rem' }}></i>
                        No previous conversations found.
                    </div>
                )}
                {!threadsLoading && chatThreads && chatThreads.length > 0 && (
                    <div className="list-group list-group-flush">
                        {chatThreads.map((thread) => (
                            <div
                                key={thread.thread_id}
                                className={`list-group-item list-group-item-action thread-item text-start d-flex justify-content-between align-items-center ${activeThreadId === thread.thread_id ? 'active' : ''}`}
                                onClick={() => onSelectThread(thread)}
                            >
                                <div className="flex-grow-1 overflow-hidden">
                                    <div className="mb-1 text-truncate">
                                        <InlineEditableText
                                            value={thread.name || 'New Chat'}
                                            onChange={(newName) => handleRenameThread(thread, newName)}
                                            placeholder="New Chat"
                                        />
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between">
                                        <small className="text-muted" title={thread.created_at}>
                                            {timeAgo(parseDateString(thread.created_at))}
                                        </small>
                                        <button
                                            className="btn btn-link btn-sm text-muted p-0 delete-btn"
                                            onClick={(e) => handleDeleteThread(e, thread)}
                                            title="Delete chat"
                                        >
                                            <i className="mdi mdi-delete-outline"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Styles>
    );
};

export default ChatHistoryList;
